/**
 * Feed demo overlay — web port of iOS `useFeedDemo` + `FeedDemoOverlay`.
 *
 * iOS sequence: swipe (horizontal, "swipe left for next part") → scroll
 * (vertical, "swipe up for the next video") → like ("click the heart button if
 * you like the video"), 3000ms per step over a full-screen rgba(0,0,0,0.7)
 * modal that is NOT dismissible by touch. The web swaps the Lottie hand
 * animations for CSS equivalents but keeps the order, copy, and timing.
 */
import { useState, useEffect, useRef } from "react";
import { ChevronUp, ChevronLeft, Heart } from "lucide-react";

const DEMO_SEEN_KEY = "feed_demo_seen";

/** iOS DEMO_STEP_DURATION (useFeedDemo.ts). */
const DEMO_STEP_DURATION = 3000;

type DemoStep = "swipe" | "scroll" | "like";

const STEP_ORDER: DemoStep[] = ["swipe", "scroll", "like"];

const STEP_TEXT: Record<DemoStep, string> = {
  swipe: "swipe left for next part",
  scroll: "swipe up for the next video",
  like: "click the heart button if you like the video",
};

interface FeedDemoOverlayProps {
  onDismiss: () => void;
}

function FeedDemoOverlay({ onDismiss }: FeedDemoOverlayProps) {
  const [stepIdx, setStepIdx] = useState(0);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // Auto-advance each step after 3s; completing the last step ends the demo
  // (iOS useFeedDemo timers). No touch dismissal — the iOS modal blocks input.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stepIdx < STEP_ORDER.length - 1) {
        setStepIdx((i) => i + 1);
      } else {
        localStorage.setItem(DEMO_SEEN_KEY, "true");
        onDismissRef.current();
      }
    }, DEMO_STEP_DURATION);
    return () => clearTimeout(timer);
  }, [stepIdx]);

  const step = STEP_ORDER[stepIdx];

  let content: React.ReactNode;
  if (step === "swipe") {
    content = (
      <div className="flex items-center">
        <ChevronLeft className="w-16 h-16 text-white animate-[feed-demo-slide-left_0.9s_ease-in-out_infinite]" />
        <ChevronLeft className="w-16 h-16 text-white/50 -ml-8 animate-[feed-demo-slide-left_0.9s_ease-in-out_infinite_150ms]" />
      </div>
    );
  } else if (step === "scroll") {
    content = (
      <div className="flex flex-col items-center">
        <ChevronUp className="w-16 h-16 text-white animate-bounce" />
        <ChevronUp className="w-16 h-16 text-white/50 -mt-8 animate-bounce [animation-delay:150ms]" />
      </div>
    );
  } else {
    // One-shot pulse + white→red fade, mirroring the iOS heart animation.
    content = (
      <div className="relative w-[100px] h-[100px] animate-[feed-demo-heart-pulse_0.8s_ease-out_forwards]">
        <Heart className="absolute inset-0 w-full h-full text-white fill-white" />
        <Heart className="absolute inset-0 w-full h-full text-[#ef4444] fill-[#ef4444] opacity-0 animate-[feed-demo-heart-red_0.4s_ease-in_forwards]" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes feed-demo-slide-left {
          0%, 100% { transform: translateX(0); opacity: 1; }
          50% { transform: translateX(-12px); opacity: 0.6; }
        }
        @keyframes feed-demo-heart-pulse {
          0% { transform: scale(1); }
          40% { transform: scale(1.3); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }
        @keyframes feed-demo-heart-red {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>

      {/* Full-screen dim — blocks interaction underneath like the iOS Modal. */}
      <div
        className="absolute inset-0 z-40 flex flex-col items-center justify-center"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
      >
        <div className="w-[250px] h-[250px] flex items-center justify-center">
          {content}
        </div>
        <p className="w-[250px] mt-8 text-white text-lg font-bold text-center">
          {STEP_TEXT[step]}
        </p>
      </div>
    </>
  );
}

export { FeedDemoOverlay };
export { DEMO_SEEN_KEY };
